// Genera y descarga un Comprobante (factura) en PDF, 100% en el navegador.
//
// IMPORTANTE: jsPDF se importa de forma ESTÁTICA (no dinámica). La generación del
// PDF y la descarga son totalmente SÍNCRONAS para conservar el "gesto del usuario":
// si hay un `await` (por ejemplo `import()` dinámico) antes de disparar la descarga,
// el navegador pierde el contexto del clic y abre una pestaña en blanco o ignora
// el atributo `download`.
import { jsPDF } from 'jspdf';
import { METHOD_LABELS } from '@/services/payments';
import type { BrandLogo } from '@/lib/brandAssets';

// Color de marca (accent) para el sello circular del encabezado.
const BRAND_ACCENT: [number, number, number] = [226, 77, 143];
const DEFAULT_WEBSITE = 'www.paticasconproposito.com';

export interface ReceiptPdfLine {
  productName: string;
  sizeLabel: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

export interface ReceiptPdfData {
  companyName: string;
  title: string;
  number: string;
  date: string;
  customerName: string;
  customerPhone?: string;
  customerCity?: string;
  customerAddress?: string;
  salespersonName?: string;
  paymentMethod: string;
  paymentStatus: string;
  items: ReceiptPdfLine[];
  total: number;
  notes?: string;
  companyPhone?: string;
  companyCity?: string;
  companyWebsite?: string;
  // Logo real de la marca (ya normalizado a PNG). Si falta, se dibuja el sello
  // circular "PCP" por defecto.
  companyLogo?: BrandLogo | null;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function statusLabel(status: string): string {
  if (status === 'approved') return 'Pagado';
  if (status === 'pending') return 'Pendiente';
  return '—';
}

export function downloadReceiptPdf(data: ReceiptPdfData): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const PW = 210;
  const margin = 14;
  const contentW = PW - margin * 2;
  let y = 18;

  function ensureSpace(h: number) {
    if (y + h > 282) {
      doc.addPage();
      y = 18;
    }
  }

  function rule(thick = 0.3) {
    doc.setLineWidth(thick);
    doc.line(margin, y, PW - margin, y);
    y += 3;
  }

  // Sello circular de marca (fallback cuando no hay logo real subido).
  function drawCircleLogo(): number {
    const r = 9;
    const cx = margin + r;
    const cy = y - 2 + r;
    doc.setFillColor(BRAND_ACCENT[0], BRAND_ACCENT[1], BRAND_ACCENT[2]);
    doc.circle(cx, cy, r, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('PCP', cx, cy + 1.4, { align: 'center' });
    return cy + r;
  }

  // ─── ENCABEZADO ───
  // Logo real de la marca si está configurado; si no, el sello circular. A la
  // derecha del logo van los datos del remitente, para que se vea oficial.
  const brandColW = 30;
  const logoBoxH = 16;
  const brandTop = y - 2;
  let brandBottom: number;

  if (data.companyLogo) {
    const { dataUrl, width, height } = data.companyLogo;
    const ratio = height > 0 ? width / height : 1;
    let drawH = logoBoxH;
    let drawW = drawH * ratio;
    if (drawW > brandColW) {
      drawW = brandColW;
      drawH = ratio > 0 ? drawW / ratio : logoBoxH;
    }
    const drawY = brandTop + (logoBoxH - drawH) / 2;
    try {
      doc.addImage(dataUrl, 'PNG', margin, drawY, drawW, drawH);
      brandBottom = brandTop + logoBoxH;
    } catch {
      brandBottom = drawCircleLogo();
    }
  } else {
    brandBottom = drawCircleLogo();
  }

  const senderX = margin + brandColW + 6;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(data.companyName || 'PCP', senderX, y + 2.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(95, 95, 95);
  const senderContact = [data.companyPhone, data.companyCity].filter(Boolean).join('  \u00b7  ');
  doc.text(senderContact || 'Bogot\u00e1 D.C., Colombia', senderX, y + 8);
  doc.text(data.companyWebsite || DEFAULT_WEBSITE, senderX, y + 12.5);

  y = brandBottom + 3;
  rule(0.6);
  y += 4;

  // Título del documento con su número
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(`${data.title} \u00b7 #${data.number}`, PW / 2, y, { align: 'center' });
  y += 5;
  rule(0.2);
  y += 4;

  // ─── DATOS DEL CLIENTE ───
  doc.setFontSize(10);
  doc.setTextColor(30, 30, 30);
  const meta: Array<[string, string]> = [
    ['Fecha', formatDate(data.date)],
    ['Cliente', data.customerName || '—'],
  ];
  if (data.customerPhone) meta.push(['Teléfono', data.customerPhone]);
  if (data.customerCity) meta.push(['Ciudad', data.customerCity]);
  if (data.salespersonName) meta.push(['Vendedor', data.salespersonName]);
  if (data.customerAddress) meta.push(['Dirección', data.customerAddress]);

  meta.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, margin, y);
    doc.setFont('helvetica', 'normal');
    const wrapped = doc.splitTextToSize(value, contentW - 28);
    doc.text(wrapped, margin + 28, y);
    y += 5 + (wrapped.length - 1) * 4.5;
  });

  y += 2;
  rule(0.3);
  y += 3;

  // ─── TABLA DE PRODUCTOS ───
  const colProduct = margin;
  const colSize = margin + 92;
  const colQty = PW - margin - 46;
  const colUnit = PW - margin - 26;
  const colSub = PW - margin;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(90, 90, 90);
  doc.text('PRODUCTO', colProduct, y);
  doc.text('TALLA', colSize, y);
  doc.text('CANT.', colQty, y, { align: 'right' });
  doc.text('P. UNIT.', colUnit, y, { align: 'right' });
  doc.text('SUBTOTAL', colSub, y, { align: 'right' });
  y += 2;
  rule(0.2);
  y += 3;

  doc.setFontSize(9);
  if (data.items.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text('Sin líneas registradas.', colProduct, y);
    y += 6;
  } else {
    data.items.forEach((it) => {
      ensureSpace(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 30, 30);
      const name = doc.splitTextToSize(it.productName || '—', 86)[0];
      doc.text(name, colProduct, y);
      doc.text((it.sizeLabel || '—').slice(0, 10), colSize, y);
      doc.text(String(it.quantity), colQty, y, { align: 'right' });
      doc.text(`$${it.unitPrice.toLocaleString()}`, colUnit, y, { align: 'right' });
      doc.setFont('helvetica', 'bold');
      doc.text(`$${it.subtotal.toLocaleString()}`, colSub, y, { align: 'right' });
      y += 6;
    });
  }

  y += 1;
  rule(0.3);
  y += 4;

  // ─── TOTAL ───
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('TOTAL', margin, y);
  doc.text(`$${data.total.toLocaleString()}`, PW - margin, y, { align: 'right' });
  y += 8;

  // ─── PAGO ───
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Medio de pago:', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(
    data.paymentMethod ? METHOD_LABELS[data.paymentMethod] || data.paymentMethod : '—',
    margin + 32,
    y,
  );
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.text('Estado:', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(statusLabel(data.paymentStatus), margin + 32, y);
  y += 8;

  if (data.notes) {
    ensureSpace(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Notas:', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    const wrapped = doc.splitTextToSize(data.notes, contentW);
    doc.text(wrapped, margin, y);
    y += wrapped.length * 4.5 + 3;
  }

  // ─── PIE ───
  const footerY = 288;
  doc.setLineWidth(0.3);
  doc.line(margin, footerY - 6, PW - margin, footerY - 6);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(60, 60, 60);
  doc.text('¡Gracias por tu compra!', PW / 2, footerY, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text('Cada paso apoya fundaciones de bienestar animal.', PW / 2, footerY + 4, {
    align: 'center',
  });

  // ─── DESCARGA ───
  // Se usa el método NATIVO de jsPDF (.save()), que baja el PDF con su tipo MIME
  // correcto (application/pdf). Así el archivo llega como PDF ejecutable y legible
  // (el sistema lo abre con el lector de PDF) y no como un binario sin formato.
  // Nombre unificado con prefijo de marca: p. ej. PCP_Comprobante_ORDER102.pdf
  const safeNumber = (data.number || 'comprobante').replace(/[^a-zA-Z0-9-]/g, '_');
  doc.save(`PCP_Comprobante_${safeNumber}.pdf`);
}